// User — the user-master design: a list of people on the left, the record open
// on the right as tabs, and Branch Transfer as a drawer.
//
// The data, permissions, search, exports and the delete call are the same as
// before; only the layout changed. The add / edit form now lives in the right
// panel (UserDetail) instead of a centred popup.
import React, { useEffect, useMemo, useState } from "react";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import { useDispatch, useSelector } from "react-redux";
import { setheadertoggle } from "../../redux/toggle";
import { fetchUser, fetchUserByBranch } from "../../redux/userServices";
import UserDetail from "../../components/adminsitration/users/UserDetail";
import UserBranchTransferDrawer from "../../components/adminsitration/users/UserBranchTransferDrawer";
import { deleteUser } from "../../services/AdminApiServices";
import { fetchBranchList } from "../../redux/branchlist";
import User from "../../core/json/User";
import {
  exportMasterExcel,
  exportMasterPDF,
  printMasterReport,
} from "../../core/utils/MasterReportUtils";
import {
  AdminScreen,
  AdminList,
  AxFilterSelect,
  AxSegments,
  AxStatusKeys,
  AxAvatar,
  AxBranchIcon,
  AxEmptyDetail,
  AxExcelIcon,
  AxPdfIcon,
  AxPlusIcon,
  AxPrintIcon,
  AxRefreshIcon,
  AxSendIcon,
  AxTableIcon,
} from "../../components/adminsitration/ui";

const SORTS = [
  { key: "name", label: "Name" },
  { key: "userRole.name", label: "Role" },
  { key: "userCode", label: "Code" },
];

const at = (record, path) =>
  String(path)
    .split(".")
    .reduce((acc, k) => (acc == null ? acc : acc[k]), record);

const UserAdmin = () => {
  const [searchText, setSearchText] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [updateData, setUpdateData] = useState({});
  const [selectedKey, setSelectedKey] = useState(null);
  const [fRole, setFRole] = useState("");
  const [fBranch, setFBranch] = useState("");
  const [seg, setSeg] = useState("all");
  const [sort, setSort] = useState({ key: "name", dir: 1 });
  const [picked, setPicked] = useState({});
  const [tableView, setTableView] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);

  const { branch, branchlist } = useSelector((state) => state.branchlist);
  const { userId } = useSelector((state) => state.userauth);
  const { userRoles } = useSelector((state) => state.usersetting);
  const { menuItems, specialpermission } = useSelector((state) => state.amc);
  const [permit, setpermit] = useState();
  const [branchpermit, setbranchpermit] = useState();
  const dispatch = useDispatch();

  useEffect(() => {
    setbranchpermit(
      specialpermission?.userSpecialPermissionList?.find(
        (e) => e.specialPermission.name == "User Branch Transfer"
      )
    );
    dispatch(fetchBranchList());
    setpermit(menuItems?.menuPermissions?.find((e) => e.menu.name == "User"));
  }, []);
  useEffect(() => {
    dispatch(fetchUserByBranch(branch.guid));
  }, [branch]);

  const { togglehead } = useSelector((state) => state.headerToggle);
  const { UserListByBranch } = useSelector((state) => state.userservice);

  const handletogglehead = () => {
    dispatch(setheadertoggle(!togglehead));
  };
  const handleSearch = (e) => {
    setSearchText(e.target.value);
  };

  /* ---- the same text search as before, plus the two filter selects ---- */
  const searched = UserListByBranch?.filter(
    (value) =>
      value.name?.toLowerCase().includes(searchText?.toLowerCase()) ||
      value.address1?.toLowerCase().includes(searchText?.toLowerCase()) ||
      value.address2?.toLowerCase().includes(searchText?.toLowerCase()) ||
      value.primaryContactNo?.toLowerCase().includes(searchText?.toLowerCase()) ||
      value.secondaryContactNo?.toLowerCase().includes(searchText?.toLowerCase()) ||
      value.email?.toLowerCase().includes(searchText?.toLowerCase()) ||
      value.userRole.name?.toLowerCase().includes(searchText?.toLowerCase()) ||
      value.userCode?.toLowerCase().includes(searchText?.toLowerCase())
  );

  const modifiedList = useMemo(() => {
    let list = searched || [];
    if (fRole) list = list.filter((u) => u.userRole?.guid === fRole);
    if (fBranch)
      list = list.filter((u) => u.assignedBranch?.some((b) => b.branch?.guid === fBranch));
    if (seg === "web") list = list.filter((u) => u.canAccessWeb);
    if (seg === "noweb") list = list.filter((u) => !u.canAccessWeb);

    const { key, dir } = sort;
    return list.slice().sort((a, b) => {
      const av = at(a, key);
      const bv = at(b, key);
      if (key === "userCode") {
        const na = Number(av);
        const nb = Number(bv);
        if (Number.isFinite(na) && Number.isFinite(nb)) return (na - nb) * dir;
      }
      return String(av ?? "").localeCompare(String(bv ?? ""), undefined, {
        sensitivity: "base",
      }) * dir;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searched, fRole, fBranch, seg, sort]);

  const counts = useMemo(() => {
    const all = searched || [];
    return {
      all: all.length,
      web: all.filter((u) => u.canAccessWeb).length,
      noweb: all.filter((u) => !u.canAccessWeb).length,
    };
  }, [searched]);

  /* ---- export columns: the same titles and dataIndex as the old grid, so
         the PDF / Excel / print output is unchanged ---- */
  const columns = [
    { title: "Name", dataIndex: "name" },
    { title: "User Code", dataIndex: "userCode" },
    { title: "User Role", dataIndex: "userRole" },
    { title: "Address 1", dataIndex: "address1" },
    { title: "Address 2", dataIndex: "address2" },
    { title: "Primary Contact", dataIndex: "primaryContactNo" },
    { title: "Secondary Contact", dataIndex: "secondaryContactNo" },
    { title: "Email", dataIndex: "email" },
  ];

  /* ---- the table view's columns ---- */
  const tableColumns = [
    {
      title: "Name",
      dataIndex: "name",
      sortKey: "name",
      render: (_, r) => (
        <div className="axa-tcust">
          <AxAvatar name={r.name} />
          <span>{r.name}</span>
        </div>
      ),
    },
    { title: "User code", dataIndex: "userCode", sortKey: "userCode", mono: true },
    {
      title: "User role",
      dataIndex: "userRole",
      sortKey: "userRole.name",
      render: (_, r) => <span className="axa-chip">{r.userRole?.name}</span>,
    },
    { title: "User name", dataIndex: "userName", mono: true },
    { title: "Address 1", dataIndex: "address1" },
    { title: "Address 2", dataIndex: "address2" },
    { title: "Primary contact", dataIndex: "primaryContactNo", mono: true },
    { title: "Secondary contact", dataIndex: "secondaryContactNo", mono: true },
    { title: "Email", dataIndex: "email" },
    {
      title: "Branches",
      dataIndex: "assignedBranch",
      render: (_, r) => <span className="mono">{r.assignedBranch?.length || 0}</span>,
    },
    {
      title: "Web login",
      dataIndex: "canAccessWeb",
      render: (_, r) =>
        r.canAccessWeb ? (
          <span className="axa-chip ok">Yes</span>
        ) : (
          <span className="axa-chip">No</span>
        ),
    },
  ];

  const handleEditClick = async (record) => {
    setModalMode("edit");
    setModalOpen(true);
    const data = UserListByBranch?.find((e) => record.guid == e.guid);
    setUpdateData(data);
    setSelectedKey(record.guid);
  };

  const MySwal = withReactContent(Swal);

  /* ---- delete: the same object and endpoint as before ---- */
  const handleDeleteClick = (guid) => {
    const data = UserListByBranch?.find((e) => guid == e.guid);
    const userObj = new User();
    userObj.AssignedBranch = data.assignedBranch;
    userObj.id = data.id;
    userObj.guid = data.guid;
    userObj.Name = data.name;
    userObj.PrimaryContactNo = data.contact1;
    userObj.SecondaryContactNo = data.contact2;
    userObj.Address1 = data.address1;
    userObj.Address2 = data.address2;
    userObj.Address3 = data.address3;
    userObj.UserRole = data.userRoles; // UserRole Class
    userObj.UserName = data.username;
    userObj.Password = data.password;
    userObj.Email = data.email;
    userObj.UserCode = data.usercode;
    userObj.CanAccessWeb = data.weblogin;
    userObj.Branch = branch;
    userObj.updatedUser = `{${userId?.id}}`;
    userObj.createdUser = `{${userId?.id}}`;
    MySwal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#00ff00",
      cancelButtonColor: "#ff0000",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await deleteUser(userObj);
          if (response?.data === true) {
            MySwal.fire({
              title: "Deleted!",
              text: "The User has been deleted.",
              icon: "success",
              confirmButtonColor: "#00ff00",
              confirmButtonText: "OK",
            });
            if (selectedKey === guid) {
              setModalOpen(false);
              setSelectedKey(null);
            }
            dispatch(fetchUserByBranch(branch.guid));
            dispatch(fetchUser());
          } else {
            MySwal.fire({
              title: "Error!",
              text: "Something went wrong.",
              icon: "error",
              confirmButtonColor: "#ff0000",
              confirmButtonText: "OK",
            });
          }
        } catch (error) {
          console.error("Error deleting user:", error);
          MySwal.fire({
            title: "Error!",
            text: "Something went wrong.",
            icon: "error",
            confirmButtonColor: "#ff0000",
            confirmButtonText: "OK",
          });
        }
      }
    });
  };

  const getActiveTabData = () => {
    const createfieldMapping = (cols) => {
      const mappings = {};
      cols.forEach((col) => {
        mappings[col.title] = col.dataIndex;
      });
      return mappings;
    };
    const fieldMapping = createfieldMapping(columns);
    return { data: modifiedList, columns: columns, fieldMapping, Rname: "User" };
  };

  const handlerefresh = () => {
    dispatch(fetchUserByBranch(branch.guid));
    setSearchText("");
    setFRole("");
    setFBranch("");
    setSeg("all");
    setPicked({});
  };

  /* ---- selection ---- */
  const pickedGuids = Object.keys(picked).filter((k) => picked[k]);
  const onPick = (key, on) =>
    setPicked((p) => {
      const next = { ...p };
      if (on) next[key] = true;
      else delete next[key];
      return next;
    });
  const onPickAll = (on) =>
    setPicked(() => {
      if (!on) return {};
      const next = {};
      modifiedList.forEach((u) => {
        next[u.guid] = true;
      });
      return next;
    });

  const bulkDelete = () => {
    const targets = modifiedList.filter((u) => picked[u.guid]);
    if (!targets.length) return;
    MySwal.fire({
      title: `Delete ${targets.length} user${targets.length === 1 ? "" : "s"}?`,
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#00ff00",
      cancelButtonColor: "#ff0000",
      confirmButtonText: "Yes, delete them!",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      let done = 0;
      for (const row of targets) {
        const userObj = new User();
        userObj.AssignedBranch = row.assignedBranch;
        userObj.id = row.id;
        userObj.guid = row.guid;
        userObj.Name = row.name;
        userObj.Branch = branch;
        userObj.updatedUser = `{${userId?.id}}`;
        userObj.createdUser = `{${userId?.id}}`;
        try {
          // eslint-disable-next-line no-await-in-loop
          const response = await deleteUser(userObj);
          if (response?.data === true) done++;
        } catch (error) {
          console.error("Error deleting user:", error);
        }
      }
      setPicked({});
      setModalOpen(false);
      setSelectedKey(null);
      dispatch(fetchUserByBranch(branch.guid));
      dispatch(fetchUser());
      MySwal.fire({
        title: done ? "Deleted!" : "Error!",
        text: `${done} of ${targets.length} deleted.`,
        icon: done ? "success" : "error",
        confirmButtonText: "OK",
      });
    });
  };

  /* ---- row -> the list row's pieces ---- */
  const renderRow = (u) => ({
    name: u.name,
    tags: null,
    sub: (
      <>
        <span className="code">{u.userCode}</span>
        <span className="axa-brc">
          <AxBranchIcon />
          {u.assignedBranch?.length || 0}
        </span>
        <span className="axa-chanchip">
          <span className={"axa-chan" + (u.canAccessWeb ? " on" : "")}>WEB</span>
        </span>
        {u.email ? <span className="mail">{u.email}</span> : null}
      </>
    ),
    right: (
      <>
        <span className="axa-chip">{u.userRole?.name}</span>
        <span className={"axa-dot " + (u.canAccessWeb ? "on" : "off")} />
      </>
    ),
  });

  const bulkBar = pickedGuids.length ? (
    <div className="axa-bulk">
      <span className="n">{pickedGuids.length} selected</span>
      {branchpermit?.userRole.id !== 0 && (
        <button type="button" onClick={() => setTransferOpen(true)}>
          <AxSendIcon />
          Transfer branch
        </button>
      )}
      {permit?.deletion && (
        <button type="button" className="del" onClick={bulkDelete}>
          Delete
        </button>
      )}
      <span className="sp" />
      <button type="button" className="plain" onClick={() => setPicked({})}>
        Clear
      </button>
    </div>
  ) : null;

  return (
    <>
      <AdminScreen
        title="User"
        subtitle="Accounts, logins and the branches each person may work in"
        searchValue={searchText}
        onSearchChange={handleSearch}
        onSearchClear={() => setSearchText("")}
        searchPlaceholder="Name, code, user name, email, phone…"
        searchHint={null}
        tableView={tableView}
        tools={
          <>
            <button
              type="button"
              className="axa-iconbtn"
              aria-pressed={tableView}
              title="Switch between the split view and the full table"
              onClick={() => setTableView((v) => !v)}
            >
              <AxTableIcon />
              {tableView ? "Split view" : "Table view"}
            </button>
            <span className="axa-sepbar" />
            <button type="button" className="axa-iconbtn" title="Reload" onClick={handlerefresh}>
              <AxRefreshIcon />
            </button>
            <button
              type="button"
              className="axa-iconbtn"
              title="Export to PDF"
              onClick={() => exportMasterPDF(getActiveTabData, "User")}
            >
              <AxPdfIcon />
              PDF
            </button>
            <button
              type="button"
              className="axa-iconbtn"
              title="Export to Excel"
              onClick={() => exportMasterExcel(getActiveTabData, "User")}
            >
              <AxExcelIcon />
              Excel
            </button>
            <button
              type="button"
              className="axa-iconbtn"
              title="Print"
              onClick={() => printMasterReport(getActiveTabData, "User")}
            >
              <AxPrintIcon />
            </button>
            <button
              type="button"
              className="axa-iconbtn"
              aria-pressed={!!togglehead}
              title="Collapse the app header"
              onClick={handletogglehead}
            >
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6">
                <path d="M4 10l4-4 4 4" />
              </svg>
            </button>
          </>
        }
        actions={
          <>
            {branchpermit?.userRole.id !== 0 && (
              <button
                type="button"
                className="axa-btn axa-btn-teal"
                onClick={() => setTransferOpen(true)}
              >
                <AxSendIcon />
                Branch Transfer
              </button>
            )}
            {permit?.add && (
              <button
                type="button"
                className="axa-btn axa-btn-primary"
                onClick={() => {
                  setModalMode("add");
                  setUpdateData({});
                  setSelectedKey(null);
                  setModalOpen(true);
                }}
              >
                <AxPlusIcon />
                New user
              </button>
            )}
          </>
        }
        filters={
          <>
            <AxFilterSelect
              value={fRole}
              onChange={(e) => setFRole(e.target.value)}
              placeholder="All roles"
              options={(userRoles || []).map((r) => ({ value: r.guid, label: r.name }))}
            />
            <AxFilterSelect
              value={fBranch}
              onChange={(e) => setFBranch(e.target.value)}
              placeholder="All branches"
              options={(branchlist || []).map((b) => ({ value: b.guid, label: b.name }))}
            />
          </>
        }
        segments={
          <AxSegments
            value={seg}
            onChange={setSeg}
            items={[
              { key: "all", label: "All", count: counts.all },
              { key: "web", label: "Web login", count: counts.web },
              { key: "noweb", label: "No web login", count: counts.noweb },
            ]}
          />
        }
        list={
          <AdminList
            items={modifiedList}
            keyOf={(u) => u.guid}
            selectedKey={selectedKey}
            onSelect={permit?.edit ? handleEditClick : undefined}
            picked={picked}
            onPick={onPick}
            onPickAll={onPickAll}
            renderRow={renderRow}
            sorts={SORTS}
            sort={sort}
            onSort={(key) =>
              setSort((s) => (s.key === key ? { key, dir: -s.dir } : { key, dir: 1 }))
            }
            bulkBar={bulkBar}
            table={tableView}
            columns={tableColumns}
            onRowEdit={permit?.edit ? handleEditClick : undefined}
            onRowDelete={permit?.deletion ? (r) => handleDeleteClick(r.guid) : undefined}
            emptyTitle="No user matches"
            emptyHint="Try a shorter search, or clear a filter."
          />
        }
        detail={
          modalOpen ? (
            <UserDetail
              key={modalMode + (updateData?.guid || "new")}
              mode={modalMode}
              data={modalMode === "edit" ? updateData : {}}
              handleClose={() => {
                setModalOpen(false);
                setSelectedKey(null);
              }}
              canDelete={!!permit?.deletion}
              onDelete={(row) => handleDeleteClick(row.guid)}
            />
          ) : (
            <AxEmptyDetail title="Pick a user">
              Choose someone on the left to see their details here. Everything that used to sit
              in the Add User window is a tab on this panel.
            </AxEmptyDetail>
          )
        }
        status={
          <AxStatusKeys
            keys={[
              { k: "Click", t: "Open a user" },
              { k: "Tick", t: "Select for transfer or delete" },
            ]}
            right={`${modifiedList.length} of ${UserListByBranch?.length || 0} users in ${
              branch?.name || "this branch"
            }`}
          />
        }
      >
        <UserBranchTransferDrawer
          open={transferOpen}
          onClose={() => setTransferOpen(false)}
          preselected={pickedGuids}
        />
      </AdminScreen>
    </>
  );
};

export default UserAdmin;
